import type { SpeciesContext } from './species.js'

export type TenantStatus = 'trial' | 'active' | 'past_due' | 'suspended' | 'cancelled'

export interface TenantSummary {
  id: number
  slug: string
  name: string
  logo_url: string | null
  status: TenantStatus
  locale: string
  timezone: string
  /** A suspended association reads and exports, and stops writing. */
  read_only: boolean
}

/** One association this account may work in, from the token's point of view. */
export interface TenantMembership {
  id: number
  slug: string
  name: string
  role: string
}

/** The person record behind the account, when there is one. */
export interface PersonSummary {
  id: number
  display_name: string
  is_breeder: boolean
}

export interface UserSummary {
  id: number
  email: string
  first_name: string | null
  last_name: string | null
  display_name: string
  locale: string
  is_platform_admin: boolean
  totp_enabled: boolean
}

/**
 * Everything the client needs to render itself for this user, in this
 * association, for these species. Returned by /me and /auth/switch-tenant.
 */
export interface SessionContext {
  user: UserSummary
  /**
   * Null when the account belongs to several associations and has not chosen
   * one yet: the tenant lives in the token, so choosing means asking for a
   * new token through switch-tenant.
   */
  tenant: TenantSummary | null
  tenants: readonly TenantMembership[]
  person?: PersonSummary | null
  role: string | null
  permissions: readonly string[]
  species: readonly SpeciesContext[]
  /** Owners and platform administrators must enrol a second factor. */
  totp_enrolment_required?: boolean
}

export function defaultSpecies(context: SessionContext | null): SpeciesContext | null {
  if (!context || context.species.length === 0) return null

  return context.species.find((species) => species.is_default) ?? context.species[0] ?? null
}

/**
 * Whether to show the species selector at all. Most associations manage one
 * species and should never see it.
 */
export function isMultiSpecies(context: SessionContext | null): boolean {
  return (context?.species.length ?? 0) > 1
}

/** A suspended tenant keeps read access and, always, data export. */
export function isReadOnly(tenant: TenantSummary | null): boolean {
  return tenant?.status === 'suspended'
}

/** Whether the user still has to pick which association to work in. */
export function needsTenantChoice(context: SessionContext | null): boolean {
  return null !== context && null === context.tenant && context.tenants.length > 0
}
